import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
	Button,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import { getPlanLogs } from "../../../../api/plan-log";
import { alertError } from "../../../../utils/logger";

const EVENT_LABELS = {
	checkout_payment: "Pago pasarela",
	checkout_subscription: "Alta / plan de pago",
	checkout_upgrade: "Mejora de plan (diferencia)",
	recurring_charge: "Cargo recurrente",
	recurring_charge_downgrade: "Recurrente + baja programada",
	scheduled_downgrade_set: "Baja programada",
	scheduled_downgrade_free: "Baja a Free (sin cargo)",
	plan_change_manual: "Cambio manual (portal)",
	admin_subscription_adjust: "Ajuste suscripción (admin)",
};

function formatEurFromCents(cents) {
	const n = parseInt(String(cents || "0"), 10);
	if (!Number.isFinite(n) || n <= 0) return "—";
	return `${(n / 100).toFixed(2).replace(".", ",")} €`;
}

function formatDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString("es-ES", {
			dateStyle: "short",
			timeStyle: "short",
		});
	} catch (e) {
		return String(iso);
	}
}

export default function PlanLogsPage() {
	const location = useLocation();
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);

	const load = useCallback(async (searchEmail) => {
		setLoading(true);
		try {
			const res = await getPlanLogs({ email: searchEmail || undefined, limit: 200 });
			if (res.status === 200 && Array.isArray(res.data)) {
				setRows(res.data);
			} else {
				setRows([]);
			}
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar los logs." });
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search || "");
		const qEmail = String(params.get("email") || "").trim();
		if (qEmail) {
			setEmail(qEmail);
			load(qEmail);
		} else {
			setEmail("");
			load("");
		}
	}, [location.search, load]);

	const onSearch = (e) => {
		e.preventDefault();
		load(email);
	};

	return (
		<>
			<Card>
				<CardHeader title="Logs de planes" />
				<CardBody>
					<form
						onSubmit={onSearch}
						style={{
							display: "flex",
							gap: 8,
							alignItems: "center",
							flexWrap: "wrap",
							marginBottom: 16,
						}}
					>
						<TextField
							size="small"
							variant="outlined"
							label="Correo del cliente"
							placeholder="buscar@ejemplo.com"
							value={email}
							onChange={(ev) => setEmail(ev.target.value)}
							style={{ minWidth: 260 }}
						/>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							startIcon={<SearchIcon />}
							disabled={loading}
						>
							Buscar
						</Button>
						<Button
							type="button"
							variant="outlined"
							disabled={loading}
							onClick={() => {
								setEmail("");
								load("");
							}}
						>
							Ver todos
						</Button>
					</form>
					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Fecha</TableCell>
										<TableCell>Cliente</TableCell>
										<TableCell>Email</TableCell>
										<TableCell>Evento</TableCell>
										<TableCell>Plan antes → después</TableCell>
										<TableCell>Importe</TableCell>
										<TableCell>Detalle</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.length === 0 ? (
										<TableRow>
											<TableCell colSpan={7} align="center">
												No hay registros.
											</TableCell>
										</TableRow>
									) : (
										rows.map((row) => (
											<TableRow key={row._id}>
												<TableCell>{formatDate(row.createdAt)}</TableCell>
												<TableCell>{row.fullName || "—"}</TableCell>
												<TableCell>{row.email || "—"}</TableCell>
												<TableCell>
													{EVENT_LABELS[row.eventType] || row.eventType}
												</TableCell>
												<TableCell>
													{row.previousRole || "—"} → {row.newRole || "—"}
												</TableCell>
												<TableCell>{formatEurFromCents(row.amountCents)}</TableCell>
												<TableCell style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>
													{row.description || "—"}
													{row.operationId ? (
														<>
															<br />
															<small style={{ color: "#666" }}>
																Op.: {row.operationId}
																{row.redsysOrder ? ` · Pedido: ${row.redsysOrder}` : ""}
																{row.cardLast4 ? ` · *${row.cardLast4}` : ""}
															</small>
														</>
													) : null}
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardBody>
			</Card>
		</>
	);
}
